import React, { useCallback, useEffect, useState } from 'react'

import styled from 'styled-components'
import Colors from '../commons/Colors'

import { Sidebar as Menu } from 'primereact/sidebar'
import CalendarIcon from '../assets/customIcons/CalendarIcon'
import Loading from '../commons/components/Loading'
import NotificationComponent from '../commons/components/screenHeader/NotificationComponent'
import { useMillContext } from '../context/MillContext'
import {
    getNotifications,
    getNotificationsConfig,
    getSources,
    updateNotificationsConfig,
} from './NewsAPI'
import NewsModalComponent from './NewsModalComponent'
import NotificationCardComponent from './NotificationCardComponent'
import NotificationDetailsModalComponent from './NotificationDetailsModalComponent'

const NewsSideMenuComponent = ({}) => {
    const {
        isModalNewsNotificationVisible,
        setIsModalNewsNotificationVisible,
    } = useMillContext()
    const [source, setSource] = useState({})
    const [notifications, setNotifications] = useState([])
    const [notificationsConfig, setNotificationsConfig] = useState({})
    const [notificationsUnread, setNotificationsUnread] = useState([])
    const [notificationsNews, setNotificationsNews] = useState([])

    const [isMenuVisible, setIsMenuVisible] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const [notificationDetail, setNotificationDetail] = useState({})
    const [
        isModalNotificationDetailVisible,
        setIsModalNotificationDetailVisible,
    ] = useState(false)

    useEffect(() => {
        loadNotifications()
    }, [])
    const loadNotifications = async () => {
        try {
            const [sourceFetch, notificationsFetch, notificationsConfigFetch] =
                await Promise.all([
                    getSources({ name: 'Portal' }),
                    getNotifications(),
                    getNotificationsConfig(),
                ])
            const sourceResponse = sourceFetch?.data
            const notificationsResponse = notificationsFetch?.data || []
            const notificationsConfigResponse =
                notificationsConfigFetch?.data || null

            setSource(sourceResponse[0])
            setNotifications(notificationsResponse)
            setNotificationsConfig(notificationsConfigResponse)

            const maxDaysForNewNotifications = 7
            const limitAt = new Date()
            limitAt.setDate(limitAt.getDate() - maxDaysForNewNotifications)

            const notificationsNewsVerified = notificationsResponse.filter(
                notification => {
                    const updatedAt = new Date(notification.updatedAt)
                    return updatedAt >= limitAt
                }
            )

            const notificationsUnReadVerified = notificationsResponse.filter(
                notification => {
                    return !notificationsConfigResponse?.viewedNotifications.includes(
                        notification.id
                    )
                }
            )

            setNotificationsNews(notificationsNewsVerified)
            setNotificationsUnread(notificationsUnReadVerified)
        } catch (error) {
            console.error('Erro ao carregar notificações:', error)
        }
    }

    const isEmptyNewsMenu = () => !Boolean(notificationsNews.length)

    const renderCalendarIcon = () => {
        const noNotificationsText = `Você já viu todas suas notificações dos últimos ${source.maxDaysToDisplay} dias`
        const notificationsText = `Estas são todas as suas notificações dos últimos ${source.maxDaysToDisplay} dias`

        return (
            <>
                <CalendarIconContainer>
                    <CalendarIcon />
                </CalendarIconContainer>
                <CalendarTextContainer>
                    <CalendarText>
                        {isEmptyNewsMenu()
                            ? noNotificationsText
                            : notificationsText}
                    </CalendarText>
                </CalendarTextContainer>
            </>
        )
    }

    const handleNewsModalOnConfirm = event => {
        event.preventDefault()
        setIsMenuVisible(!isMenuVisible)
        setIsModalNewsNotificationVisible(false)
    }

    const handleNewsModalOnClose = () => {
        setIsMenuVisible(!isMenuVisible)
        setIsModalNewsNotificationVisible(false)
    }

    const handleNotificationOnClick = useCallback(
        async notification => {
            try {
                setIsLoading(true)
                if (isNotificationUnRead(notification.id)) {
                    const updatedViewedNotifications = [
                        ...notificationsConfig.viewedNotifications,
                        notification.id,
                    ]
                    const updatedNotificationsConfig = {
                        ...notificationsConfig,
                        viewedNotifications: updatedViewedNotifications,
                    }
                    await updateNotificationsConfig(updatedNotificationsConfig)
                    setNotificationsConfig(updatedNotificationsConfig)
                    setNotificationsUnread(prev =>
                        prev.filter(unread => unread.id !== notification.id)
                    )
                }
                setNotificationDetail(notification)
                setIsMenuVisible(false)
                setIsModalNotificationDetailVisible(true)
            } catch (error) {
                console.error(
                    'Erro ao atualizar configuração de notificações:',
                    error
                )
            } finally {
                setIsLoading(false)
            }
        },
        [notificationsConfig, notificationsUnread]
    )

    const handleModalNotificationDetailOnClose = () => {
        setIsModalNotificationDetailVisible(!isModalNotificationDetailVisible)
    }

    const isNotificationNew = id => {
        const news = notificationsNews.find(
            notificationNew => notificationNew.id === id
        )
        return Boolean(news)
    }

    const isNotificationUnRead = id => {
        const unRead = notificationsUnread.find(
            notificationUnread => notificationUnread.id === id
        )
        return Boolean(unRead)
    }

    return (
        <>
            <Container>
                <NotificationComponent
                    onClick={() =>
                        !isModalNewsNotificationVisible &&
                        !isModalNotificationDetailVisible &&
                        setIsMenuVisible(!isMenuVisible)
                    }
                    counter={notificationsUnread.length}
                />
                <Sidebar
                    dismissable={false}
                    showCloseIcon={false}
                    modal={false}
                    closeOnEscape={true}
                    position="right"
                    visible={isMenuVisible}
                    onHide={() => setIsMenuVisible(false)}
                >
                    <SideBarContent>
                        <TitleContainer>
                            <Title>Notificações de Novidades</Title>
                        </TitleContainer>
                        {isEmptyNewsMenu() && <Divider />}
                        <CardsContainer>
                            <ListContainer>
                                {notifications?.map(notification => (
                                    <NotificationCardComponent
                                        key={notification.id}
                                        notification={notification}
                                        handleClick={() =>
                                            handleNotificationOnClick(
                                                notification
                                            )
                                        }
                                        isNotificationNew={isNotificationNew(
                                            notification.id
                                        )}
                                        isNotificationUnRead={isNotificationUnRead(
                                            notification.id
                                        )}
                                    />
                                ))}
                            </ListContainer>
                        </CardsContainer>
                        <CalendarInformationContainer
                            isEmpty={isEmptyNewsMenu()}
                        >
                            {renderCalendarIcon()}
                        </CalendarInformationContainer>
                    </SideBarContent>
                </Sidebar>
                <NewsModalComponent
                    isVisible={
                        notificationsUnread?.length > 0 &&
                        isModalNewsNotificationVisible
                    }
                    onConfirm={handleNewsModalOnConfirm}
                    onClose={handleNewsModalOnClose}
                />
                <NotificationDetailsModalComponent
                    isNotificationNew={isNotificationNew(notificationDetail.id)}
                    isVisible={isModalNotificationDetailVisible}
                    notification={notificationDetail}
                    onClose={handleModalNotificationDetailOnClose}
                />
                {isLoading && (
                    <LoadingContainer>
                        <Loading>Atualizando...</Loading>
                    </LoadingContainer>
                )}
            </Container>
        </>
    )
}

const Container = styled.div``

const Sidebar = styled(Menu)`
    margin-top: 65px;
    height: calc(100vh - 60px);
    padding: 0 !important;
    width: 400px;
    overflow: auto;
`

const SideBarContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`

const TitleContainer = styled.div`
    padding: 15px 0 15px 0;
    text-align: center;
`

const Title = styled.span`
    color: ${Colors.orangeTwo};
`

const CardsContainer = styled.div`
    display: flex;
    width: 100%
    align-items: center;
`

const Divider = styled.div`
    height: 1px;
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    background-color: rgba(199, 199, 204, 0.5);
`

const CalendarInformationContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
    height: ${({ isEmpty }) => (isEmpty ? '100%' : 'auto')};
    padding-top: ${({ isEmpty }) => (isEmpty ? 0 : '35px')};
    padding-bottom: ${({ isEmpty }) => (isEmpty ? 0 : '35px')};
`
const CalendarIconContainer = styled.div``

const CalendarTextContainer = styled.div`
    width: 55%;
    padding-top: 20px;
`
const CalendarText = styled.span`
    font-family: Roboto;
    font-size: 11px;
    font-weight: 600;
    color: ${Colors.brownishGrey};
`
const ListContainer = styled.div`
    width: 100%;
`

const LoadingContainer = styled.div`
    align-items: center;
    justify-content: center;
    height: 10vh;
`

export default NewsSideMenuComponent
